import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  Plus,
  Calendar,
  Clock,
  User,
  FileText,
  Search,
  Filter,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import LeaveApplication from './LeaveApplication';
import { leavesApi, LeaveApplication as LeaveApp, LeaveBalance } from '../../services/api/leaves';
import toast from 'react-hot-toast';

interface LeaveManagementProps {
  isManager?: boolean;
}

const LeaveManagement: React.FC<LeaveManagementProps> = ({
  isManager = false,
}) => {

  const [applications, setApplications] = useState<LeaveApp[]>([]);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<LeaveApp | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    startDate: '',
    endDate: '',
  });

  // Fetch leave applications
  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = isManager
        ? await leavesApi.getLeaveApplications()
        : await leavesApi.getMyLeaveApplications();

      setApplications(response.applications || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setApplications([]);
      toast.error('Failed to load leave applications');
    } finally {
      setLoading(false);
    }
  };

  // Fetch leave balances
  const fetchBalances = async () => {
    try {
      const response = await leavesApi.getLeaveBalance();
      setBalances(response || []);
    } catch (error) {
      console.error('Error fetching balances:', error);
      setBalances([]);
      toast.error('Failed to load leave balances');
    }
  };

  // Fetch both applications and balances
  const fetchData = async () => {
    await Promise.all([fetchApplications(), fetchBalances()]);
  };

  useEffect(() => {
    fetchData();
  }, [isManager]);

  // Handle application submitted
  const handleApplicationSubmitted = (application: LeaveApp) => {
    setApplications(prev => [application, ...prev]);
    setIsModalOpen(false);
    fetchData(); // Refresh data
    toast.success('Leave application submitted successfully!');
  };

  // Filter applications
  const filteredApplications = (applications || []).filter(app => {
    if (filters.status && app.status !== filters.status) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch =
        app.reason?.toLowerCase().includes(searchLower) ||
        (app.leaveType?.name?.toLowerCase().includes(searchLower) ||
         (typeof app.leaveTypeId === 'object' && (app.leaveTypeId as any)?.name?.toLowerCase().includes(searchLower))) ||
        app.user?.firstName?.toLowerCase().includes(searchLower) ||
        app.user?.lastName?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }
    return true;
  });

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'APPROVED':
        return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
      case 'REJECTED':
        return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
      case 'CANCELLED':
        return <Badge className="bg-gray-100 text-gray-800">Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'APPROVED':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  // Format date
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Calculate leave duration
  const calculateDuration = (startDate: string | undefined, endDate: string | undefined, isHalfDay: boolean) => {
    if (!startDate || !endDate) return 0;
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return isHalfDay ? 0.5 : diffDays;
    } catch (error) {
      return 0;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Leave Management</h1>
          <p className="text-muted-foreground">
            {isManager ? 'Manage team leave applications' : 'Apply for leaves and track your applications'}
          </p>
        </div>
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="h-4 w-4 mr-2" />
              Apply for Leave
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Apply for Leave</DialogTitle>
            </DialogHeader>
            <LeaveApplication
              onApplicationSubmitted={handleApplicationSubmitted}
              showBalance={true}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Leave Balance Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Calendar className="h-5 w-5" />
                <span>Leave Balance</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (balances || []).length > 0 ? (
                <div className="space-y-4">
                  {(balances || []).map((balance, index) => (
                    <div key={balance.id || index} className="p-3 border rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">
                          {(() => {
                            // Try different ways to get the leave type name
                            if (balance.leaveType?.name) return balance.leaveType.name;
                            if (typeof balance.leaveTypeId === 'object' && balance.leaveTypeId && (balance.leaveTypeId as any).name) {
                              return (balance.leaveTypeId as any).name;
                            }
                            return 'Unknown';
                          })()}
                        </span>
                        <Badge variant="outline">{balance.available || 0} days</Badge>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex justify-between">
                          <span>Total:</span>
                          <span>{balance.totalAllotted || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Used:</span>
                          <span>{balance.used || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Pending:</span>
                          <span>{balance.pending || 0}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No leave balance data available</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Leave Applications Section */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>{isManager ? 'Team Leave Applications' : 'My Leave Applications'}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search applications..."
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                  placeholder="All Status"
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectContent>
                      <SelectItem value="">All Status</SelectItem>
                      <SelectItem value="PENDING">Pending</SelectItem>
                      <SelectItem value="APPROVED">Approved</SelectItem>
                      <SelectItem value="REJECTED">Rejected</SelectItem>
                      <SelectItem value="CANCELLED">Cancelled</SelectItem>
                    </SelectContent>
                  </SelectTrigger>
                </Select>
                <Button
                  variant="outline"
                  onClick={() => setFilters({ status: '', search: '', startDate: '', endDate: '' })}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              </div>

              {/* Applications List */}
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  <span>Loading applications...</span>
                </div>
              ) : filteredApplications.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No leave applications found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredApplications.map((application) => (
                    <div
                      key={application.id}
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            {getStatusIcon(application.status || 'PENDING')}
                            <div className="flex items-center space-x-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{
                                  backgroundColor: (() => {
                                    // Check both leaveType and leaveTypeId (populated) fields
                                    if (application.leaveType?.color) return application.leaveType.color;
                                    if (typeof application.leaveTypeId === 'object' && (application.leaveTypeId as any)?.color) {
                                      return (application.leaveTypeId as any).color;
                                    }
                                    return '#gray';
                                  })()
                                }}
                              />
                              <span className="font-medium">
                                {(() => {
                                  // Check both leaveType and leaveTypeId (populated) fields
                                  if (application.leaveType?.name) return application.leaveType.name;
                                  if (typeof application.leaveTypeId === 'object' && (application.leaveTypeId as any)?.name) {
                                    return (application.leaveTypeId as any).name;
                                  }
                                  return 'Unknown';
                                })()}
                              </span>
                            </div>
                            {getStatusBadge(application.status || 'PENDING')}
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Duration:</span>
                              <div className="font-medium">
                                {formatDate(application.startDate)} - {formatDate(application.endDate)}
                                <span className="ml-2 text-muted-foreground">
                                  ({calculateDuration(application.startDate, application.endDate, application.isHalfDay)} days)
                                </span>
                              </div>
                            </div>
                            
                            {isManager && application.user && (
                              <div>
                                <span className="text-muted-foreground">Employee:</span>
                                <div className="font-medium">
                                  {application.user.firstName} {application.user.lastName}
                                </div>
                              </div>
                            )}
                            
                            <div>
                              <span className="text-muted-foreground">Applied:</span>
                              <div className="font-medium">
                                {formatDate(application.appliedAt)}
                              </div>
                            </div>
                          </div>
                          
                          <div className="mt-2">
                            <span className="text-muted-foreground text-sm">Reason:</span>
                            <p className="text-sm mt-1">{application.reason || 'No reason provided'}</p>
                          </div>

                          {/* Approval Status Information */}
                          {(() => {
                            const getApprovalStatusInfo = () => {
                              if (application.status === 'PENDING' && application.approvalWorkflow?.levels) {
                                const currentLevel = application.approvalWorkflow.currentLevel;
                                const currentLevelInfo = application.approvalWorkflow.levels.find(level => level.level === currentLevel);

                                if (currentLevelInfo && typeof currentLevelInfo.approverId === 'object') {
                                  const approver = currentLevelInfo.approverId;
                                  const approverName = `${approver.firstName} ${approver.lastName}`;
                                  const approverRole = currentLevelInfo.approverRole === 'FirmAdmin' ? 'Firm Admin' : currentLevelInfo.approverRole;

                                  return (
                                    <div className="mt-2 p-2 bg-yellow-50 rounded border-l-4 border-yellow-200">
                                      <span className="text-yellow-800 text-sm font-medium">Pending with:</span>
                                      <p className="text-yellow-700 text-sm mt-1">{approverName} ({approverRole})</p>
                                    </div>
                                  );
                                }
                              } else if (application.status === 'APPROVED' && application.approvalWorkflow?.levels) {
                                // Find the last approved level
                                const approvedLevels = application.approvalWorkflow.levels.filter(level => level.status === 'approved');
                                const lastApprovedLevel = approvedLevels[approvedLevels.length - 1];

                                if (lastApprovedLevel && typeof lastApprovedLevel.approverId === 'object') {
                                  const approver = lastApprovedLevel.approverId;
                                  const approverName = `${approver.firstName} ${approver.lastName}`;
                                  const approverRole = lastApprovedLevel.approverRole === 'FirmAdmin' ? 'Firm Admin' : lastApprovedLevel.approverRole;

                                  return (
                                    <div className="mt-2 p-2 bg-green-50 rounded border-l-4 border-green-200">
                                      <span className="text-green-800 text-sm font-medium">Approved by:</span>
                                      <p className="text-green-700 text-sm mt-1">{approverName} ({approverRole})</p>
                                    </div>
                                  );
                                }
                              }
                              return null;
                            };

                            return getApprovalStatusInfo();
                          })()}

                          {application.rejectionReason && (
                            <div className="mt-2 p-2 bg-red-50 rounded border-l-4 border-red-200">
                              <span className="text-red-800 text-sm font-medium">Rejection Reason:</span>
                              <p className="text-red-700 text-sm mt-1">{application.rejectionReason}</p>
                            </div>
                          )}
                        </div>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedApplication(application);
                            setIsViewModalOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* View Application Modal */}
        <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Leave Application Details</DialogTitle>
            </DialogHeader>
            {selectedApplication && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Leave Type</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor: (() => {
                            if (selectedApplication.leaveType?.color) return selectedApplication.leaveType.color;
                            if (typeof selectedApplication.leaveTypeId === 'object' && (selectedApplication.leaveTypeId as any)?.color) {
                              return (selectedApplication.leaveTypeId as any).color;
                            }
                            return '#gray';
                          })()
                        }}
                      />
                      <span className="font-medium">
                        {(() => {
                          if (selectedApplication.leaveType?.name) return selectedApplication.leaveType.name;
                          if (typeof selectedApplication.leaveTypeId === 'object' && (selectedApplication.leaveTypeId as any)?.name) {
                            return (selectedApplication.leaveTypeId as any).name;
                          }
                          return 'Unknown';
                        })()}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Status</label>
                    <div className="mt-1">
                      {getStatusBadge(selectedApplication.status || 'PENDING')}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Start Date</label>
                    <p className="mt-1 font-medium">
                      {new Date(selectedApplication.startDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">End Date</label>
                    <p className="mt-1 font-medium">
                      {new Date(selectedApplication.endDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Duration</label>
                    <p className="mt-1 font-medium">
                      {Math.ceil((new Date(selectedApplication.endDate).getTime() - new Date(selectedApplication.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1} days
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Applied Date</label>
                    <p className="mt-1 font-medium">
                      {new Date(selectedApplication.appliedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">Reason</label>
                  <p className="mt-1 p-3 bg-gray-50 rounded-lg">
                    {selectedApplication.reason}
                  </p>
                </div>

                {selectedApplication.rejectionReason && (
                  <div>
                    <label className="text-sm font-medium text-red-600">Rejection Reason</label>
                    <p className="mt-1 p-3 bg-red-50 rounded-lg text-red-800">
                      {selectedApplication.rejectionReason}
                    </p>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => setIsViewModalOpen(false)}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default LeaveManagement;
